import { supabase, supabaseConfigured } from "./supabaseClient";
import { extractDiscordIdentity } from "./discordIdentity";
import { normalizeSteamId } from "./steamId";
import { translate } from "@/i18n/I18nProvider";

/**
 * @typedef {Object} PlayerProfile
 * @property {string} id
 * @property {string | null} username
 * @property {string | null} display_name
 * @property {string | null} avatar_url
 * @property {string | null} discord_user_id
 * @property {string | null} steam_id
 */

/**
 * @param {import('@supabase/supabase-js').User} user
 * @returns {Promise<PlayerProfile | null>}
 */
export async function ensurePlayerProfile(user) {
    if (!supabaseConfigured || !supabase || !user?.id) return null;

    const { data: existing, error: readErr } = await supabase
        .from("profiles")
        .select("id, username, display_name, avatar_url, discord_user_id, steam_id")
        .eq("id", user.id)
        .maybeSingle();

    if (readErr) {
        console.warn("[playerProfile] read", readErr);
        return null;
    }

    if (existing) return existing;

    const discord = extractDiscordIdentity(user);
    const row = {
        id: user.id,
        email: user.email ?? null,
        username: discord.discordUsername,
        display_name: discord.discordGlobalName,
        avatar_url: discord.discordAvatarUrl,
        discord_user_id: discord.discordUserId,
    };

    const { data: inserted, error: insertErr } = await supabase
        .from("profiles")
        .insert(row)
        .select("id, username, display_name, avatar_url, discord_user_id, steam_id")
        .single();

    if (insertErr) {
        console.warn("[playerProfile] insert", insertErr);
        return null;
    }

    return inserted;
}

/**
 * @param {import('@supabase/supabase-js').User} user
 * @returns {Promise<PlayerProfile | null>}
 */
export async function fetchPlayerProfile(user) {
    if (!supabaseConfigured || !supabase || !user?.id) return null;

    const { data, error } = await supabase
        .from("profiles")
        .select("id, username, display_name, avatar_url, discord_user_id, steam_id")
        .eq("id", user.id)
        .maybeSingle();

    if (error) {
        console.warn("[playerProfile] fetch", error);
        return null;
    }

    return data;
}

/**
 * @param {import('@supabase/supabase-js').User} [user] — necessário se o perfil ainda não existir em `profiles`
 * @param {string} userId
 * @param {string} steamIdRaw
 */
export async function updatePlayerSteamId(userId, steamIdRaw, user) {
    if (!supabaseConfigured || !supabase) {
        return { ok: false, error: translate("auth.error.supabaseNotConfigured") };
    }

    const steam_id = normalizeSteamId(steamIdRaw);
    if (!steam_id) {
        return {
            ok: false,
            error: translate("auth.error.invalidSteamId"),
        };
    }

    let existing = await fetchPlayerProfile({ id: userId });
    if (!existing && user) {
        existing = await ensurePlayerProfile(user);
    }

    const discord = user ? extractDiscordIdentity(user, existing) : {};
    const baseRow = {
        id: userId,
        email: user?.email ?? null,
        username: discord.discordUsername ?? existing?.username ?? null,
        display_name: discord.discordGlobalName ?? existing?.display_name ?? null,
        avatar_url: discord.discordAvatarUrl ?? existing?.avatar_url ?? null,
        discord_user_id: discord.discordUserId ?? existing?.discord_user_id ?? null,
        steam_id,
    };

    const { data, error } = await supabase
        .from("profiles")
        .upsert(baseRow, { onConflict: "id" })
        .select("id, username, display_name, avatar_url, discord_user_id, steam_id")
        .maybeSingle();

    if (error) {
        if (error.code === "23505") {
            return { ok: false, error: translate("auth.error.steamIdTaken") };
        }
        return { ok: false, error: error.message || translate("auth.error.steamIdSaveFailed") };
    }

    if (!data) {
        return { ok: false, error: translate("auth.error.profileNotFound") };
    }

    return { ok: true, profile: data };
}

/**
 * @param {import('@supabase/supabase-js').User | null | undefined} user
 * @param {PlayerProfile | null | undefined} profile
 */
export function buildPlayerIdentityForInvoice(user, profile) {
    const discord = extractDiscordIdentity(user, profile);
    const steamId = normalizeSteamId(profile?.steam_id) || profile?.steam_id || "";

    return {
        ...discord,
        steamId,
        profileId: profile?.id ?? user?.id ?? null,
    };
}

/**
 * @param {import('@supabase/supabase-js').User | null | undefined} user
 * @param {PlayerProfile | null | undefined} profile
 */
export function canAccessShopOrSimulator(user, profile) {
    if (!user?.id) return { ok: false, reason: "login" };
    const steamId = normalizeSteamId(profile?.steam_id);
    if (!steamId) return { ok: false, reason: "steam" };
    return { ok: true, steamId };
}
